<template>
  <div class="relative">
    <textarea
      :id="id"
      ref="textarea"
      v-model="currentValue"
      :autofocus="autofocus"
      :disabled="disabled"
      :name="name"
      :placeholder="placeholder"
      :readonly="readonly"
      :required="required"
      :maxlength="maxlength"
      :rows="rows"
      :wrap="wrap"
      :style="autoresize ? { overflowY: 'hidden' } : undefined"
      :class="[
        currentClass,
        counter && maxlength ? 'rounded-b-none border-b-0' : ''
      ]"
      @blur="onBlur"
      @focus="onFocus"
      @keyup="$emit('keyup', $event)"
      @keydown="$emit('keydown', $event)"
    />

    <div v-if="counter && maxlength" class="flex justify-end text-xs font-medium bg-gray-50 rounded-b-md py-1 px-2 border dark:bg-gray-900">
      {{ count }} / {{ maxlength }}
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  modelValue?: string | null
  required?: boolean
  placeholder?: string | null
  maxlength?: string | number | null
  readonly?: boolean
  rows?: string | number
  wrap?: string
  counter?: boolean
  autoresize?: boolean
  baseClass?: string | Record<string, any> | string[]
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
  // attributes mixin
  id?: string | number | null
  autofocus?: boolean | null
  disabled?: boolean
  name?: string | null
  tabindex?: string | number | null
  // classes mixin
  size?: string | null
  status?: boolean | string | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  required: false,
  placeholder: null,
  maxlength: null,
  readonly: undefined,
  rows: 4,
  wrap: 'soft',
  counter: false,
  autoresize: false,
  baseClass: 'w-full block bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-md focus-within:ring-2 rounded-md text-sm',
  defaultStatusClass: 'bg-white dark:bg-gray-900',
  warningStatusClass: 'border-yellow-400 bg-yellow-100 dark:bg-yellow-400/20',
  errorStatusClass: 'border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border-green-300 bg-green-100 dark:bg-green-400/20',
  disabledClass: 'bg-gray-50 cursor-not-allowed opacity-75 dark:bg-gray-900',
  defaultSizeClass: 'p-2',
  largeSizeClass: 'p-4 text-lg',
  smallSizeClass: 'p-2 text-sm',
  id: null,
  autofocus: null,
  disabled: false,
  name: null,
  tabindex: null,
  size: null,
  status: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'blur', value: any): void
  (e: 'focus', value: any): void
  (e: 'change', value: any): void
  (e: 'keyup', value: any): void
  (e: 'keydown', value: any): void
}>()

const textarea = ref<HTMLTextAreaElement>()

const currentValue = ref(props.modelValue)
const valueWhenFocus = ref<any>(null)
let resizeObserver: ResizeObserver | null = null

const count = computed(() => {
  return currentValue.value?.length || 0
})

// -- classes mixin --
const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (props.size === null && props.defaultSizeClass) classes.push(props.defaultSizeClass)
  else if (props.size === 'sm' && props.smallSizeClass) classes.push(props.smallSizeClass)
  else if (props.size === 'lg' && props.largeSizeClass) classes.push(props.largeSizeClass)
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

const currentClass = computed(() => {
  const classes: any[] = [props.baseClass]
  return classes.concat(statusClasses.value)
})

watch(() => props.modelValue, (value) => {
  currentValue.value = value
})

watch(currentValue, (val) => {
  emit('update:modelValue', val)

  if (props.autoresize) {
    nextTick(resize)
  }
})

watch(() => props.autoresize, (autoresize) => {
  if (autoresize) {
    nextTick(resize)
  }
})

function resize () {
  const textareaElement = textarea.value
  if (!textareaElement) return

  textareaElement.style.height = 'auto'
  textareaElement.style.height = `${textareaElement.scrollHeight}px`
}

function onBlur (e: Event) {
  emit('blur', e)
  if (currentValue.value !== valueWhenFocus.value) {
    emit('change', currentValue.value)
  }
}

function onFocus (e: Event) {
  emit('focus', e)
  valueWhenFocus.value = currentValue.value
}

function isSafari () {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

function handleSafariResize (element: HTMLElement) {
  const ogValue = element.style.overflowY
  element.style.overflowY = ogValue === 'visible' ? 'auto' : 'visible'
  element.style.overflowY = ogValue
}

function attachResizeObserver () {
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        requestAnimationFrame(() => {
          handleSafariResize(entry.target as HTMLElement)
        })
      }
    })

    const textareaElement = textarea.value
    if (textareaElement) {
      resizeObserver.observe(textareaElement)
    }
  }
}

function detachResizeObserver () {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
}

onMounted(() => {
  if (props.autoresize) {
    resize()
  }

  if (isSafari()) {
    attachResizeObserver()
  }
})

onBeforeUnmount(() => {
  if (isSafari()) {
    detachResizeObserver()
  }
})
</script>
